<template>
  <div>
    <v-list class="pa-0">
      <template v-for="resource in resources">
        <v-list-tile
          :key="`resource_${resource.ownerProfileId}`"
          @click="openRootFolder(resource)"
          :class="[
            'listItem isResource',
            {
              'grey lighten-2': !isSelected(resource.creativeFolder),
              'blue lighten-2 isSelected': isSelected(resource.creativeFolder),
            },
          ]"
        >
          <v-list-tile-action>
            <v-icon
              v-text="folderIcon(resource)"
              :color="
                isSelected(resource.creativeFolder)
                  ? 'white'
                  : folderColor(resource)
              "
              :class="['folderIcon', 'text--lighten-3', { isSelected }]"
              @click.stop="toggleResource(resource)"
            />
          </v-list-tile-action>
          <v-list-tile-content>{{ resource.name }}</v-list-tile-content>
        </v-list-tile>
        <v-list-tile
          v-if="isLoadingResource(resource)"
          :key="`resource_${resource.ownerProfileId}_loading`"
          class="listItem"
        >
          <v-btn loading flat style="width: 100%;" />
        </v-list-tile>
        <template v-if="isOpenResource(resource)">
          <v-list-tile
            v-for="folder in resource.creativeFolder.children"
            :key="`folder_${folder.id}`"
            @click.stop="selectFolder(folder)"
            :class="[
              'listItem',
              { 'blue lighten-2 isSelected': isSelected(folder) },
            ]"
          >
            <v-list-tile-action />
            <v-list-tile-content>{{ folder.name }}</v-list-tile-content>
          </v-list-tile>
        </template>
        <v-divider :key="`divider_${resource.ownerProfileId}`" class="white" />
      </template>
    </v-list>
  </div>
</template>

<script>
import union from 'lodash/union'
import difference from 'lodash/difference'
import { AdvertiserModel } from '@/models/Advertiser'

export default {
  name: 'FolderSelector',
  props: {
    value: {
      type: Object,
      default: null,
    },
    resourceOpen: {
      type: Object,
      default: null,
    },
    resources: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      open: [],
      loading: [],
    }
  },
  computed: {
    selected: {
      get() {
        return this.value
      },
      set(folder) {
        this.$emit('input', folder)
      },
    },
  },
  async created() {
    if (this.resourceOpen != null) {
      this.openResource(this.resourceOpen)
    }
  },
  methods: {
    isSelected(folder) {
      if (this.selected == null || folder == null) return false
      return this.selected.id === folder.id
    },
    folderColor(resource) {
      return this.isAdvertiser(resource) ? 'pink' : 'primary'
    },
    isAdvertiser(resource) {
      return resource instanceof AdvertiserModel
    },
    toggleResource(resource) {
      if (this.isOpenResource(resource)) {
        this.closeResource(resource)
      } else {
        this.openResource(resource)
      }
    },
    async openResource(resource) {
      try {
        this.loading = union(this.loading, [resource.ownerProfileId])
        await resource.fetchCreativeFolders()
        this.open = union(this.open, [resource.ownerProfileId])
      } finally {
        this.loading = difference(this.loading, [resource.ownerProfileId])
      }
    },
    closeResource(resource) {
      this.open = difference(this.open, [resource.ownerProfileId])
    },
    isOpenResource(resource) {
      return this.open.includes(resource.ownerProfileId)
    },
    async openRootFolder(resource) {
      if (this.isOpenResource(resource)) {
        const rootFolder = resource.creativeFolder
        this.selectFolder(rootFolder)
      } else {
        await this.openResource(resource)
        const rootFolder = resource.creativeFolder
        const currentSubFolder = resource.creativeFolder.children.find(
          c => this.value && c.id === this.value.id,
        )
        const currentFolder = currentSubFolder || rootFolder
        if (currentFolder) {
          this.selectFolder(currentFolder)
        }
      }
    },
    selectFolder(folder) {
      this.selected = folder
    },
    folderIcon(resource) {
      return this.isOpenResource(resource) ? 'folder_open' : 'folder'
    },
    isLoadingResource(resource) {
      return this.loading.includes(resource.ownerProfileId)
    },
  },
}
</script>

<style scoped lang="scss">
.listItem {
  ::v-deep .v-list__tile--link {
    text-decoration: none !important;
  }

  &.isResource ::v-deep {
    .v-list__tile__content,
    .v-list__tile__action {
      color: #333 !important;
    }
  }

  &.isSelected ::v-deep {
    .v-list__tile__content,
    .v-list__tile__action {
      color: white !important;
    }
  }
}

.folderIcon {
  opacity: 0.4;

  &.isSelected {
    opacity: 1;
  }
}
</style>
